import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";
import { useAuth } from "../../../../context/AuthContext"; // Ajustez le chemin selon votre structure

const AddUserModal = ({ isOpen, onClose, onAddUser }) => {
  const { user: currentUser } = useAuth();
  const [isAdminUser, setIsAdminUser] = useState(false);

  // Définir l'URL de base de l'API dans une constante
  const API_BASE_URL = "https://api.greenecobagsusa.com/api";

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    telephone: "",
    password: "",
    password_confirmation: "",
    // Utiliser is_admin au lieu de isAdmin pour correspondre au backend
    is_admin: false
  });

  const [loading, setLoading] = useState(false);

  // Vérifier si l'utilisateur est admin dès que currentUser est disponible
  useEffect(() => {
    if (currentUser) {
      // Vérifier les deux formats possibles
      const adminStatus =
        currentUser.is_admin === 1 ||
        currentUser.is_admin === true ||
        currentUser.isAdmin === true ||
        currentUser.isAdmin === 1;

      setIsAdminUser(adminStatus);

      // Afficher les informations pour le débogage
      console.log("Utilisateur actuel:", currentUser);
      console.log("Statut admin:", adminStatus);
    }
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    // Utiliser is_admin au lieu de isAdmin
    setFormData((prev) => ({ ...prev, is_admin: e.target.checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.password_confirmation) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    // Vérifier l'authentification et les privilèges administrateur
    if (!currentUser) {
      toast.error("Vous n'êtes pas authentifié");
      return;
    }

    if (!isAdminUser) {
      toast.error("Vous n'avez pas les privilèges administrateur requis");
      return;
    }

    setLoading(true);

    // Convertir is_admin en nombre pour correspondre à ce que le backend attend
    const dataToSend = {
      ...formData,
      is_admin: formData.is_admin ? 1 : 0 // Convertir en 1 ou 0
    };

    try {
      // Correction de l'URL d'API
      const response = await axios.post(`${API_BASE_URL}/users`, dataToSend);

      toast.success("Utilisateur ajouté avec succès");
      if (onAddUser && response.data.user) {
        onAddUser(response.data.user);
      }
      setFormData({
        name: "",
        email: "",
        telephone: "",
        password: "",
        password_confirmation: "",
        is_admin: false
      });
      onClose();
    } catch (error) {
      console.error("Erreur:", error);

      if (error.response) {
        if (error.response.status === 401) {
          toast.error("Non autorisé. Veuillez vous reconnecter.");
        } else if (error.response.status === 403) {
          toast.error("Accès refusé. Privilèges administrateur requis.");
          console.log("Réponse d'erreur complète:", error.response.data);
        } else {
          toast.error(error.response.data.message || "Erreur lors de l'ajout de l'utilisateur");

          if (error.response.data.errors) {
            Object.values(error.response.data.errors).forEach(errorMsg => {
              toast.error(Array.isArray(errorMsg) ? errorMsg[0] : errorMsg);
            });
          }
        }
      } else {
        toast.error("Erreur de connexion au serveur");
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Add a user</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {!isAdminUser && currentUser && (
          <div className="mb-4 p-2 bg-red-100 text-red-700 rounded-md">
            Warning: You do not have administrator privileges.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className="w-full p-3 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full p-3 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telephone *
            </label>
            <input
              type="tel"
              name="telephone"
              value={formData.telephone}
              onChange={handleInputChange}
              className="w-full p-3 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password *
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full p-3 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password *
            </label>
            <input
              type="password"
              name="password_confirmation"
              value={formData.password_confirmation}
              onChange={handleInputChange}
              className="w-full p-3 border rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              name="is_admin"
              checked={formData.is_admin}
              onChange={handleCheckboxChange}
              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
            />
            <label className="text-sm font-medium text-gray-700">
              Administrator Account
            </label>
          </div>

          <div className="mt-6 flex justify-end space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? "Creating..." : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUserModal;